import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaTachometerAlt, FaQuestion, FaUsers, FaBars, FaTimes,
  FaFutbol, FaSignOutAlt, FaTrophy, FaCog, FaMedal, FaGift, FaStar, FaCoins, FaShoppingCart
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';

const NAV_ITEMS = [
  { path: '/admin',               label: 'Dashboard',    Icon: FaTachometerAlt, exact: true },
  { path: '/admin/questions',     label: 'Questions',    Icon: FaQuestion },
  { path: '/admin/users',         label: 'Utilisateurs', Icon: FaUsers },
  { path: '/admin/tournaments',   label: 'Tournois',     Icon: FaTrophy },
  { path: '/admin/championships', label: 'Championnats', Icon: FaMedal },
  { path: '/admin/referral',      label: 'Parrainage',   Icon: FaGift },
  { path: '/admin/coins',         label: 'Pièces',       Icon: FaCoins },
  { path: '/admin/shop',          label: 'Boutique',     Icon: FaShoppingCart },
  { path: '/admin/themes',        label: 'Thèmes',       Icon: FaStar },
  { path: '/admin/settings',      label: 'Paramètres',   Icon: FaCog },
];

function AdminLayout() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className="min-h-screen bg-qfl-dark flex">
      {/* Sidebar desktop */}
      <div className="hidden md:flex flex-col w-60 bg-qfl-card border-r border-green-900 min-h-screen">
        <SidebarContent user={user} onLogout={handleLogout} />
      </div>

      {/* Sidebar mobile (drawer) */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/60 z-40 md:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSidebarOpen(false)}
            />
            <motion.div
              className="fixed left-0 top-0 bottom-0 w-60 bg-qfl-card border-r border-green-900 z-50 md:hidden"
              initial={{ x: -240 }}
              animate={{ x: 0 }}
              exit={{ x: -240 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            >
              <SidebarContent user={user} onLogout={handleLogout} onClose={() => setSidebarOpen(false)} />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Contenu principal */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-green-900 bg-qfl-card/50">
          <button
            className="md:hidden text-white/60 hover:text-white"
            onClick={() => setSidebarOpen(true)}
          >
            <FaBars size={20} />
          </button>
          <h2
            className="text-qfl-gold font-bold uppercase tracking-widest text-sm"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            Administration OM QUIZ
          </h2>
        </div>

        {/* Page */}
        <div className="flex-1 overflow-auto p-4 md:p-6">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

function SidebarContent({ user, onLogout, onClose }) {
  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center justify-between px-4 py-5 border-b border-green-900">
        <div className="flex items-center gap-2">
          <FaFutbol size={20} className="text-qfl-gold" />
          <span
            className="font-black text-qfl-gold uppercase tracking-wider text-sm"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            QFL Admin
          </span>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-white/40 hover:text-white">
            <FaTimes size={16} />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-4 flex flex-col gap-1">
        {NAV_ITEMS.map(({ path, label, Icon, exact }) => (
          <NavLink
            key={path}
            to={path}
            end={exact}
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                isActive
                  ? 'bg-qfl-gold/20 text-qfl-gold border border-qfl-gold/30'
                  : 'text-white/60 hover:text-white hover:bg-white/10'
              }`
            }
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            <Icon size={16} />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* User + déconnexion */}
      <div className="px-3 py-4 border-t border-green-900">
        <div className="flex items-center gap-2 px-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-qfl-gold/20 flex items-center justify-center text-qfl-gold font-bold text-sm" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            {user?.pseudo?.[0]?.toUpperCase() || 'A'}
          </div>
          <div>
            <p className="text-white text-xs font-bold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              {user?.pseudo || 'Admin'}
            </p>
            <p className="text-white/40 text-xs">Administrateur</p>
          </div>
        </div>
        <button
          onClick={() => window.open(window.location.origin, '_blank')}
          className="w-full flex items-center gap-2 px-3 py-2 text-white/40 hover:text-white/70 hover:bg-white/5 rounded-lg text-sm transition-colors mb-1"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}
        >
          <FaFutbol size={14} />
          Voir le site
        </button>
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-2 px-3 py-2 text-qfl-red/80 hover:text-qfl-red hover:bg-qfl-red/10 rounded-lg text-sm transition-colors"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}
        >
          <FaSignOutAlt size={14} />
          Déconnexion
        </button>
      </div>
    </div>
  );
}

export default AdminLayout;
